import { ASSETS_HOST } from "replo-utils/lib/assets";
import { MINIAPP_CATALOG } from "replo-utils/lib/miniappCatalog";
import * as z from "zod";

export const PRODUCT_ASSET_KEY_PREFIX = "product-assets/";
export const PRODUCT_ASSET_MAX_BYTES = 50 * 1024 * 1024;
export const PRODUCT_ASSET_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "video/mp4",
  "video/webm",
] as const;

export const PRODUCT_ASSET_APP_TAG_PREFIX = "app:";

/** Tags everyone should reach for first. Anyone may still add a new tag by using it. */
export const PRODUCT_ASSET_TAG_VOCABULARY = {
  device: ["desktop", "mobile"],
  kind: ["screenshot", "video", "gif", "graphic", "diagram"],
  surface: ["agent", "theme-builder", "admin", "published-site"],
  usedFor: [
    "announcement",
    "newsletter",
    "docs",
    "social",
    "blog",
    "changelog",
    "sales",
  ],
  state: [
    "empty-state",
    "loading-state",
    "error-state",
    "onboarding",
    "billing",
    "upsell",
    "cancellation",
    "paywall",
    "settings",
  ],
  app: Object.keys(MINIAPP_CATALOG).map((miniappId) => {
    return `${PRODUCT_ASSET_APP_TAG_PREFIX}${miniappId}`;
  }),
} as const satisfies Record<string, readonly string[]>;

/** Quality is the asset's status, not a tag. These two are dropped wherever they still arrive. */
const RETIRED_PRODUCT_ASSET_TAGS = new Set(["marketing", "internal"]);

export function isRetiredProductAssetTag(tag: string): boolean {
  return RETIRED_PRODUCT_ASSET_TAGS.has(tag);
}

export const PRODUCT_ASSET_STATUSES = [
  "approved",
  "ready",
  "reference",
] as const;
type ProductAssetStatus = (typeof PRODUCT_ASSET_STATUSES)[number];

/** Approved is a person's call and wins; ready is an agent's opinion; everything else is reference. */
export function getProductAssetStatus({
  approvedAt,
  ready,
}: {
  approvedAt: string | null;
  ready: boolean | null;
}): ProductAssetStatus {
  if (approvedAt) {
    return "approved";
  }
  return ready ? "ready" : "reference";
}

const productAssetMimeTypeSchema = z.enum(PRODUCT_ASSET_MIME_TYPES);

/** Which job made the file. It only names the storage folder; tags are what surface an asset. */
export const PRODUCT_ASSET_SOURCES = [
  "docs",
  "announcements",
  "marketing",
] as const;

export const productAssetUploadSchema = z.object({
  fileName: z.string().trim().min(1).max(200),
  mimeType: productAssetMimeTypeSchema,
  sizeBytes: z.number().int().positive().max(PRODUCT_ASSET_MAX_BYTES),
  source: z.enum(PRODUCT_ASSET_SOURCES).optional(),
});

/** What an asset was made from (a Remotion project, a design file), as one zip people can download and edit. */
const PRODUCT_ASSET_PROJECT_FILE_FOLDER = "project-files/";
const PRODUCT_ASSET_PROJECT_FILE_MAX_BYTES = 2 * 1024 * 1024 * 1024;
const projectFileNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .regex(/\.zip$/i, "Project files are one .zip");

export const productAssetProjectFileUploadSchema = z.object({
  fileName: projectFileNameSchema,
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(PRODUCT_ASSET_PROJECT_FILE_MAX_BYTES),
});

export const productAssetUploadResultSchema = z.object({
  signedUploadUrl: z.url(),
  url: z.url(),
});

/** The storage key for one upload: the source folder when given, an unguessable folder, then a readable file name. */
export function getProductAssetKey({
  id,
  fileName,
  source,
}: {
  id: string;
  fileName: string;
  source?: (typeof PRODUCT_ASSET_SOURCES)[number];
}): string {
  const safeName =
    fileName
      .toLowerCase()
      .replaceAll(/[^a-z0-9.]+/g, "-")
      .replaceAll(/^-|-$/g, "")
      .slice(-80) || "asset";
  return `${PRODUCT_ASSET_KEY_PREFIX}${source ? `${source}/` : ""}${id}/${safeName}`;
}

function isShelfPath(url: string, prefix: string): boolean {
  if (!URL.canParse(url)) {
    return false;
  }
  const parsed = new URL(url);
  return (
    parsed.protocol === "https:" &&
    parsed.hostname === ASSETS_HOST &&
    parsed.pathname.startsWith(`/${prefix}`)
  );
}

export function isProductAssetProjectFileUrl(url: string): boolean {
  return isShelfPath(
    url,
    `${PRODUCT_ASSET_KEY_PREFIX}${PRODUCT_ASSET_PROJECT_FILE_FOLDER}`,
  );
}

/** Only files we uploaded ourselves can become assets; anything else could rot or be swapped. A project zip is not a picture. */
export function isProductAssetUrl(url: string): boolean {
  return (
    isShelfPath(url, PRODUCT_ASSET_KEY_PREFIX) &&
    !isProductAssetProjectFileUrl(url)
  );
}

export function getProductAssetProjectFileKey({
  id,
  fileName,
}: {
  id: string;
  fileName: string;
}): string {
  return getProductAssetKey({ id, fileName }).replace(
    PRODUCT_ASSET_KEY_PREFIX,
    `${PRODUCT_ASSET_KEY_PREFIX}${PRODUCT_ASSET_PROJECT_FILE_FOLDER}`,
  );
}

const productAssetProjectFileSchema = z.object({
  url: z
    .url()
    .refine(
      isProductAssetProjectFileUrl,
      "Upload the zip with product_asset_project_file_upload_url first, then pass the url it returns",
    ),
  fileName: projectFileNameSchema,
  sizeBytes: z.number().int().positive(),
});

export function normalizeProductAssetTag(tag: string): string {
  const [head, ...rest] = tag.trim().toLowerCase().split(":");
  const slug = (value: string) => {
    return value.replaceAll(/[^a-z0-9]+/g, "-").replaceAll(/^-|-$/g, "");
  };
  // "app:brand-studio" keeps its namespace; everything else is one kebab-case word.
  return rest.length && head === "app"
    ? `${PRODUCT_ASSET_APP_TAG_PREFIX}${slug(rest.join(":"))}`
    : slug(tag.trim().toLowerCase());
}

export function getProductAssetKindTag(
  mimeType: string,
): "gif" | "screenshot" | "video" {
  if (mimeType.startsWith("video/")) {
    return "video";
  }
  return mimeType === "image/gif" ? "gif" : "screenshot";
}

/** Applies the shelf's rules: kebab-case, no duplicates, no retired tags, at most one device, and a kind. */
export function getProductAssetTags({
  tags,
  suggested = [],
  mimeType,
}: {
  tags: readonly string[];
  /** From the vision step. Never overrides what the caller said. */
  suggested?: readonly string[];
  mimeType: string;
}): string[] {
  const isKept = (tag: string) => {
    return Boolean(tag) && !RETIRED_PRODUCT_ASSET_TAGS.has(tag);
  };
  const chosen = tags.map(normalizeProductAssetTag).filter(isKept);
  const has = (group: readonly string[]) => {
    return chosen.some((tag) => group.includes(tag));
  };
  const { device, kind } = PRODUCT_ASSET_TAG_VOCABULARY;
  const extras = suggested
    .map(normalizeProductAssetTag)
    .filter(isKept)
    .filter((tag) => {
      if (device.some((known) => known === tag)) {
        return !has(device);
      }
      // The file type decides screenshot, video or gif; a picture of a video player is not a video.
      if (kind.some((known) => known === tag)) {
        return (
          !has(kind) &&
          mimeType.startsWith("image/") &&
          (tag === "graphic" || tag === "diagram")
        );
      }
      if (tag.startsWith(PRODUCT_ASSET_APP_TAG_PREFIX)) {
        return !chosen.some((existing) => {
          return existing.startsWith(PRODUCT_ASSET_APP_TAG_PREFIX);
        });
      }
      return true;
    });
  const merged = [...new Set([...chosen, ...extras])];
  if (!merged.some((tag) => kind.some((known) => known === tag))) {
    merged.push(getProductAssetKindTag(mimeType));
  }
  return merged;
}

const tagListSchema = z.array(z.string().trim().min(1).max(60)).max(30);

function refineTagRules(
  tags: readonly string[] | undefined,
  context: z.RefinementCtx,
) {
  const devices = (tags ?? []).map(normalizeProductAssetTag).filter((tag) => {
    return PRODUCT_ASSET_TAG_VOCABULARY.device.some((known) => known === tag);
  });
  if (devices.length > 1) {
    context.addIssue({
      code: "custom",
      path: ["tags"],
      message: "Pick desktop or mobile, not both",
    });
  }
}

/** An agent's own call on whether the capture is usable as it is. It wins over Gemini's. */
const readyFields = {
  ready: z.boolean().optional(),
  readyNotes: z.string().trim().min(1).max(400).optional(),
};

export const productAssetCreateSchema = z
  .object({
    url: z
      .url()
      .refine(
        isProductAssetUrl,
        "Upload the file with product_asset_upload_url first, then pass the url it returns",
      ),
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).nullish(),
    tags: tagListSchema.optional(),
    mimeType: productAssetMimeTypeSchema.optional(),
    /** Where it was captured or what it documents: a PR, a page, a session. */
    sourceUrl: z.url().max(2000).nullish(),
    ...readyFields,
  })
  .superRefine((value, context) => refineTagRules(value.tags, context));

export const productAssetUpdateSchema = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    description: z.string().trim().max(2000).nullish(),
    tags: tagListSchema.optional(),
    archived: z.boolean().optional(),
    /** Look at the file again: new caption, ready opinion and embedding. */
    redescribe: z.boolean().optional(),
    ...readyFields,
    /** A person's decision. Only a signed-in Admin session may send it. */
    approved: z.boolean().optional(),
    /** Attach what the asset was made from; null detaches it. */
    projectFile: productAssetProjectFileSchema.nullish(),
  })
  .superRefine((value, context) => refineTagRules(value.tags, context));

export const productAssetSearchSchema = z.object({
  query: z.string().trim().max(500).optional(),
  tags: z.array(z.string()).max(20).optional(),
  /** approved: a person said use it. ready: approved, or an agent thinks it is usable. all: everything. */
  status: z.enum(["approved", "ready", "all"]).default("all"),
  limit: z.coerce.number().int().min(1).max(100).default(48),
  includeArchived: z.boolean().default(false),
});

export const productAssetSchema = z.object({
  id: z.uuid(),
  url: z.url(),
  title: z.string(),
  description: z.string().nullable(),
  caption: z.string().nullable(),
  status: z.enum(PRODUCT_ASSET_STATUSES),
  /** An agent's opinion on whether a user-facing page could use this as it is; null when nobody has looked. */
  ready: z.boolean().nullable(),
  readyNotes: z.string().nullable(),
  approvedAt: z.iso.datetime().nullable(),
  approvedBy: z.string().nullable(),
  mimeType: z.string(),
  sizeBytes: z.number().nullable(),
  tags: z.array(z.string()),
  sourceUrl: z.string().nullable(),
  projectFile: productAssetProjectFileSchema.nullable(),
  createdBy: z.string(),
  createdAt: z.iso.datetime(),
  archivedAt: z.iso.datetime().nullable(),
  /** 0–1 cosine similarity to the query; null when the list is not a meaning search. */
  similarity: z.number().nullable(),
});
export type ProductAsset = z.infer<typeof productAssetSchema>;
